import { getAuthUser } from "./lib/effectiveAuthUser";
import firebase from "firebase/app";
import "firebase/auth";
import { CONTENT_POLICY_ERROR_CODE, CONTENT_POLICY_ERROR, CONTENT_GENERATION_FAILED } from "../../src/shared/constants";
import { ContentViolationError } from "./lib/Errors";
import * as Sentry from "@sentry/react";
import { uniqueId } from "lodash";
import { matchPath } from "react-router-dom";
import { clearEmailReviewRestriction, recordEmailReviewRestriction } from "./stores/emailReviewStore";
import { isGenerationRequest } from "../../src/shared/isAIGenerationRequest";

// This is essential to have abort event being sent to the server, otherwise
// those will be sent to the proxy and will be lost.
//
// Only rewrite when the page itself is served from localhost. When the dev
// frontend is accessed via a public tunnel (ngrok, Cloudflare Tunnel, etc.),
// keep the relative path so the browser doesn't block with private-network-
// access CORS (SW-8868 ngrok-based geolocation testing).
function bypassProxyInDev(url) {
  if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
    const hostname = window.location?.hostname;
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
    if (isLocalhost && url?.[0] === "/") {
      const apiHost = process.env.REACT_APP_API_HOST || "http://localhost:5000";
      return apiHost + url;
    }
  }

  return url;
}

// Attach the frontend build/version marker to API calls. This makes Sentry and
// server logs easier to correlate when a bad deployment only affects one client
// bundle.
const getVersionHeader = () => {
  return { "x-sudowrite-client-version": window.sudowriteClientVersion || "loading" };
};

export class AuthedFetchError extends Error {
  constructor(message, response, status, endpointUrl = null, responseText = null) {
    super(message);
    this.name = "AuthedFetchError";
    this.response = response;
    this.status = status;
    this.endpointUrl = endpointUrl || response?.url || null;
    this.responseText = responseText;
  }
}

class NetworkPolicyBlockedError extends Error {
  constructor(message, response, status, endpointUrl, responseText, fromCooldown = false) {
    super(message);
    this.name = "NetworkPolicyBlockedError";
    this.response = response;
    this.status = status;
    this.endpointUrl = endpointUrl;
    this.responseText = responseText;
    this.isNetworkPolicyBlocked = true;
    this.fromCooldown = fromCooldown;
  }
}

export class RateLimitedError extends Error {
  constructor() {
    super("Too many requests");
    this.name = "RateLimtedError";
  }
}

const networkPolicyBlockedUrls = new Set();
const capturedNetworkPolicyBlockedUrls = new Set();
const RESPONSE_TEXT_PREVIEW_LIMIT = 1000;
const SENTRY_TAG_VALUE_LIMIT = 200;
const RESPONSE_READ_FAILURE_RETRY_DELAY_MS = 250;
const LOGOUT_PATH_PREFIX = "/logout";

export function markLogoutInProgress() {
  if (typeof window === "undefined") {
    return;
  }

  window.sudowriteLogoutInProgress = true;
}

function isLogoutInProgress() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.sudowriteLogoutInProgress === true || window.location?.pathname?.startsWith(LOGOUT_PATH_PREFIX);
}

/** Avoid surfacing `new Error({ error: ... })` → "[object Object]" from stream payloads. */
function streamPayloadErrorMessage(errorField) {
  if (errorField == null) return null;
  if (typeof errorField === "string" && errorField.trim()) {
    return errorField.trim();
  }
  if (typeof errorField === "object") {
    if (typeof errorField.message === "string" && errorField.message.trim()) {
      return errorField.message.trim();
    }
    if (typeof errorField.error === "string" && errorField.error.trim()) {
      return errorField.error.trim();
    }
    if (errorField.error && typeof errorField.error === "object") {
      const nested = streamPayloadErrorMessage(errorField.error);
      if (nested) return nested;
    }
  }
  return null;
}

const NETWORK_BLOCK_PATTERNS = [
  /<title>[^<]*(blocked|access denied|forbidden)[^<]*<\/title>/i,
  /\bweb page blocked\b/i,
  /\baccess to (this|the) (web page|site|url).{0,120}\bblocked\b/i,
  /\b(url|web|content)\s*filter(ing)?\b/i,
  /\b(blocked|denied|forbidden)\s+by\s+(policy|administrator|organization|network)\b/i,
  /\bthis request was blocked\b/i,
];

// Detect school/workplace/network-filter block pages that arrive as successful
// or error HTML responses instead of JSON. These need a user-actionable message
// and should be cached briefly so repeated calls fail fast.
function isNetworkPolicyBlockedResponse(responseText = "", contentType = "") {
  const lowerContentType = String(contentType || "").toLowerCase();
  const looksLikeHtml = lowerContentType.includes("text/html");
  if (!looksLikeHtml && !responseText) return false;
  return NETWORK_BLOCK_PATTERNS.some((pattern) => pattern.test(responseText));
}

// Report a network-policy block once per endpoint so Sentry shows breadth
// without getting flooded by every retry or every hook polling the same URL.
function shouldCaptureNetworkPolicyBlocked(endpointUrl = "unknown") {
  if (capturedNetworkPolicyBlockedUrls.has(endpointUrl)) {
    return false;
  }
  capturedNetworkPolicyBlockedUrls.add(endpointUrl);
  return true;
}

// Headers can be absent or backed by browser objects that throw in edge cases.
// Normalize header reads to an empty string so diagnostics code never masks the
// original fetch failure.
function safeHeader(response, headerName) {
  try {
    return response?.headers?.get?.(headerName) || "";
  } catch {
    return "";
  }
}

// Snapshot response fields before body consumption so later Sentry context can
// still explain failures where `response.text()` itself rejects. Keep this data
// primitive and small because it is copied into both tags and Sentry extras.
function getResponseMetadata(response) {
  if (!response) {
    return {};
  }

  return {
    status: response.status,
    statusText: response.statusText || "",
    url: response.url || "",
    redirected: response.redirected,
    type: response.type || "",
    bodyUsed: response.bodyUsed,
    contentType: safeHeader(response, "content-type"),
    contentLength: safeHeader(response, "content-length"),
    contentEncoding: safeHeader(response, "content-encoding"),
    cfRay: safeHeader(response, "cf-ray"),
    renderOriginServer: safeHeader(response, "x-render-origin-server"),
  };
}

// Sentry tags have a hard length limit and reject null-ish values. This keeps
// tag generation consistent across fetch, upload, retry, and stream reporting.
function formatSentryTag(value) {
  if (value == null || value === "") {
    return undefined;
  }

  return String(value).slice(0, SENTRY_TAG_VALUE_LIMIT);
}

// Apply Sentry's tag formatting uniformly instead of repeating
// `formatSentryTag(...)` for each field in every context builder.
function formatSentryTags(values) {
  return Object.entries(values).reduce((tags, [key, value]) => {
    const formattedValue = formatSentryTag(value);
    if (formattedValue !== undefined) {
      tags[key] = formattedValue;
    }
    return tags;
  }, {});
}

// Promise-based sleep used for the short, opt-in response-read retry backoff.
// It intentionally does not own abort handling; callers check `signal.aborted`.
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Retry telemetry is 1-based and counts retry attempts, not total fetch
// attempts. The original request is attemptIndex 0, the first retry is
// attemptIndex 1, and so on. Omit this context when no retry has actually run.
function getResponseReadRetryContext(attemptIndex, responseReadFailureRetries) {
  if (!responseReadFailureRetries || attemptIndex <= 0) {
    return {};
  }

  return {
    responseReadRetryAttempt: attemptIndex,
    responseReadRetryMax: responseReadFailureRetries,
  };
}

// Convert numeric options from caller-provided values without allowing negative
// retry counts or fractional attempts.
function getNonNegativeInteger(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : fallback;
}

// Operational controls belong outside the JSON request body. Keep them in the
// fourth options argument so backend handlers only receive real endpoint params.
// This is intentionally strict: callers must pass requestSource/retry controls
// here rather than smuggling client-only fields through endpoint params.
function getAuthedFetchOptions(options = {}) {
  options = options || {};
  const requestSource = options.requestSource;
  const suppressAbortSentry = options.suppressAbortSentry;
  const responseReadFailureRetries = getNonNegativeInteger(
    options.responseReadFailureRetries,
    0,
  );
  const responseReadFailureRetryDelayMs = getNonNegativeInteger(
    options.responseReadFailureRetryDelayMs,
    RESPONSE_READ_FAILURE_RETRY_DELAY_MS,
  );

  return {
    requestSource,
    suppressAbortSentry,
    responseReadFailureRetries,
    responseReadFailureRetryDelayMs,
  };
}

// Enrich an AuthedFetch-style error with the stable diagnostic fields our UI and
// Sentry filters rely on. This separates "could not read the body" from "read an
// empty/malformed body", which was the core ambiguity behind the regression.
function applyResponseDiagnostics(error, { response, endpointUrl, responseText = null, responseReadError, responseFailureKind, parseError, responseMetadata } = {}) {
  responseMetadata = responseMetadata || getResponseMetadata(response);
  error.endpointUrl = endpointUrl || error.endpointUrl || responseMetadata.url || null;
  error.responseMetadata = responseMetadata;
  error.responseFailureKind = responseFailureKind || error.responseFailureKind || null;
  error.responseTextLength = typeof responseText === "string" ? responseText.length : null;
  error.responseBodyReadFailed = !!responseReadError;

  if (responseReadError) {
    error.responseReadErrorName = responseReadError.name || responseReadError.constructor?.name || "";
    error.responseReadErrorMessage = responseReadError.message || String(responseReadError);
    error.cause = responseReadError;
  }

  if (parseError) {
    error.responseParseErrorName = parseError.name || parseError.constructor?.name || "";
    error.responseParseErrorMessage = parseError.message || String(parseError);
    error.cause = parseError;
  }

  return error;
}

// Build a generic API/JSON failure while keeping the parser's classification on
// the Error object. Fetch, upload, and stream call sites can then report the same
// diagnostics without trying to reread the body.
function makeAuthedFetchError(message, details = {}) {
  const { response, endpointUrl, responseText = null } = details;
  return applyResponseDiagnostics(new AuthedFetchError(message, response, response?.status, response?.url || endpointUrl, responseText), {
    ...details,
    endpointUrl: response?.url || endpointUrl,
    responseText,
  });
}

// Build the specialized error shown when an institutional network returns a
// block page. It carries the same diagnostic fields as AuthedFetchError plus a
// flag used to suppress duplicate reports.
function makeNetworkPolicyBlockedError(message, details = {}) {
  const { response, endpointUrl, responseText = null, fromCooldown = false } = details;
  return applyResponseDiagnostics(new NetworkPolicyBlockedError(message, response, response?.status || 0, response?.url || endpointUrl, responseText, fromCooldown), {
    ...details,
    endpointUrl: response?.url || endpointUrl,
    responseText,
    responseFailureKind: "network-policy-blocked",
  });
}

// Identify HTML before JSON parsing so auth/API callers get a clear
// "unexpected HTML" or "network policy blocked" failure instead of a generic
// JSON.parse exception.
function responseLooksLikeHtml(responseText = "", contentType = "") {
  const trimmedResponseText = String(responseText || "").trim();
  return (
    String(contentType).toLowerCase().includes("text/html") ||
    /^<!doctype\b/i.test(trimmedResponseText) ||
    /^<html[\s>]/i.test(trimmedResponseText)
  );
}

// Read the response body for parsing or diagnostics. A failed body read used to
// look identical to a successfully read empty body; returning the read error
// separately lets Sentry distinguish browser/network stream failures from true
// empty responses.
async function readResponseTextForDiagnostics(response, { clone = true } = {}) {
  if (!response) {
    return { responseText: null, responseReadError: null };
  }

  try {
    const readableResponse = clone ? response.clone() : response;
    return { responseText: await readableResponse.text(), responseReadError: null };
  } catch (responseReadError) {
    return { responseText: null, responseReadError };
  }
}

// Parse JSON responses through one classification path. All JSON-response
// endpoints flow through this parser, including file uploads, so response-read
// failures, empty bodies, HTML, network policy pages, and malformed JSON stay
// grouped by the real failure mode instead of collapsing into one exception.
export async function parseJsonResponse(response, endpointUrl) {
  const responseMetadata = getResponseMetadata(response);
  const contentType = responseMetadata.contentType || "";
  const { responseText, responseReadError } = await readResponseTextForDiagnostics(response, { clone: false });

  if (responseReadError) {
    throw makeAuthedFetchError("Unable to read JSON response body from server.", {
      response,
      endpointUrl,
      responseFailureKind: "response-read-failed",
      responseReadError,
      responseMetadata,
    });
  }

  const looksLikeHtml = responseLooksLikeHtml(responseText, contentType);

  if (looksLikeHtml && isNetworkPolicyBlockedResponse(responseText, contentType)) {
    networkPolicyBlockedUrls.add(endpointUrl);
    throw makeNetworkPolicyBlockedError("Network policy blocked this request. Please switch networks or contact your administrator.", {
      response,
      endpointUrl,
      responseText,
      responseMetadata,
    });
  }

  if (looksLikeHtml) {
    throw makeAuthedFetchError("Unexpected HTML response from server.", {
      response,
      endpointUrl,
      responseText,
      responseFailureKind: "html-response",
      responseMetadata,
    });
  }

  if (!responseText.trim()) {
    throw makeAuthedFetchError("Empty JSON response from server.", {
      response,
      endpointUrl,
      responseText,
      responseFailureKind: "empty-json",
      responseMetadata,
    });
  }

  try {
    return JSON.parse(responseText);
  } catch (parseError) {
    throw makeAuthedFetchError("Invalid JSON response from server.", {
      response,
      endpointUrl,
      responseText,
      responseFailureKind: "invalid-json",
      responseMetadata,
      parseError,
    });
  }
}

// Convert non-OK HTTP responses into AuthedFetchError while preserving any body
// diagnostics already read by the caller. This avoids consuming the response
// stream twice on error responses.
async function createServerProblemError(response, endpointUrl, readResult = null) {
  const { responseText, responseReadError } = readResult || (await readResponseTextForDiagnostics(response));
  let serverMessage = "Server problem.";
  let serverCode;
  if (typeof responseText === "string" && responseText.trim()) {
    try {
      const parsed = JSON.parse(responseText);
      serverCode = typeof parsed?.code === "string" ? parsed.code : undefined;
      const parsedErrorString = typeof parsed?.error === "string" ? parsed.error : null;
      serverMessage =
        parsed?.errorMessage ||
        parsed?.message ||
        parsed?.details?.message ||
        parsedErrorString ||
        serverMessage;
    } catch {
      // Keep the generic fallback when the body isn't JSON.
    }
  }

  return Object.assign(makeAuthedFetchError(serverMessage, {
    response,
    endpointUrl,
    responseText,
    responseFailureKind: "http-error",
    responseReadError,
  }), { code: serverCode });
}

// Keep the diagnostic fields that belong in Sentry `extra` in one place. Tag
// fields are derived from this where possible so additions do not require
// maintaining two parallel lists by hand.
function getResponseDiagnosticsForSentry(error, responseMetadata) {
  return {
    responseTextLength: error.responseTextLength,
    responseMetadata,
    responseFailureKind: error.responseFailureKind || null,
    responseBodyReadFailed: !!error.responseBodyReadFailed,
    responseReadErrorName: error.responseReadErrorName,
    responseReadErrorMessage: error.responseReadErrorMessage,
    responseParseErrorName: error.responseParseErrorName,
    responseParseErrorMessage: error.responseParseErrorMessage,
  };
}

// Only attach response text for HTML-like failures. JSON bodies can be large and
// user/content-bearing, while HTML block pages are short and useful to classify.
function getResponseTextPreviewForSentry(error) {
  if (error.responseFailureKind !== "html-response" && error.responseFailureKind !== "network-policy-blocked") {
    return undefined;
  }

  return String(error.responseText || "").slice(0, RESPONSE_TEXT_PREVIEW_LIMIT);
}

// Shape Sentry context consistently for fetch, upload, stream, and retry
// telemetry. Tags stay low-cardinality and bounded; richer details live in
// `extra` for debugging individual events.
function getAuthedFetchSentryContext(error, { endpointUrl, requestSource, responseReadRetryAttempt, responseReadRetryMax, responseReadRetryRecovered } = {}) {
  const responseMetadata = error.responseMetadata || getResponseMetadata(error.response);
  const responseDiagnostics = getResponseDiagnosticsForSentry(error, responseMetadata);
  const responseReadRetry = {
    responseReadRetryAttempt,
    responseReadRetryMax,
    responseReadRetryRecovered: !!responseReadRetryRecovered,
  };
  const extra = {
    ...responseDiagnostics,
    requestSource,
    networkPolicyBlocked: !!error.isNetworkPolicyBlocked,
    networkPolicyFromCooldown: !!error.fromCooldown,
    ...responseReadRetry,
  };
  const responseTextPreview = getResponseTextPreviewForSentry(error);

  if (responseTextPreview !== undefined) {
    extra.responseTextPreview = responseTextPreview;
  }

  return {
    extra,
    tags: formatSentryTags({
      endpointUrl,
      statusCode: error.response?.status,
      requestSource,
      networkPolicyBlocked: extra.networkPolicyBlocked,
      responseFailureKind: responseDiagnostics.responseFailureKind,
      responseContentType: responseMetadata.contentType,
      responseContentLength: responseMetadata.contentLength,
      responseBodyReadFailed: responseDiagnostics.responseBodyReadFailed,
      responseReadRetryAttempt,
      responseReadRetryMax,
      responseReadRetryRecovered,
    }),
  };
}

// Capture only frontend-owned failures. Backend/API response errors should be
// handled by callers or backend Sentry; client-side body read failures, network
// policy pages, and malformed browser-visible responses remain reportable here.
function isAbortErrorForSentrySuppression(error) {
  return (
    error?.name === "AbortError" ||
    error?.cause?.name === "AbortError" ||
    error?.responseReadErrorName === "AbortError"
  );
}

async function captureAuthedFetchException(error, { endpointUrl, requestSource, suppressAbortSentry = false, ...responseRetryContext } = {}) {
  const shouldCaptureAbort = !(suppressAbortSentry && isAbortErrorForSentrySuppression(error));
  const shouldSuppressLogoutResponseReadFailure = isResponseReadFailure(error) && isLogoutInProgress();
  // Response-backed API failures should be handled by the caller and/or
  // backend Sentry. Keep this capture path for true browser/client failures
  // like network policy blocks, parse errors, and other unexpected throws.
  const shouldCapture =
    shouldCaptureAbort &&
    !shouldSuppressLogoutResponseReadFailure &&
    !isExpectedApiResponseError(error) &&
    (!error.isNetworkPolicyBlocked || shouldCaptureNetworkPolicyBlocked(endpointUrl));

  if (!shouldCapture) {
    return;
  }

  Sentry.captureException(error, getAuthedFetchSentryContext(error, { endpointUrl, requestSource, ...responseRetryContext }));
}

// Wrap capture so telemetry never replaces the user-facing API error path. In
// particular, stream callers rely on `onError` firing even if Sentry or fallback
// diagnostics fail while reporting the original exception.
async function captureAuthedFetchExceptionSafely(error, options = {}) {
  try {
    await captureAuthedFetchException(error, options);
  } catch {
    // Preserve the original API error path even if telemetry reporting fails.
  }
}

// Emit a positive signal when an opt-in retry recovers from a response body read
// failure. This gives us visibility into flaky browser/network reads without
// creating a user-visible error or hiding the eventual successful response.
async function captureResponseReadRetryRecoverySafely(error, options = {}) {
  try {
    const context = getAuthedFetchSentryContext(error, { ...options, responseReadRetryRecovered: true });
    Sentry.captureMessage(
      "AuthedFetch recovered after response body read failure retry.",
      {
        ...context,
        level: "warning",
      },
    );
  } catch {
    // Recovery telemetry is best-effort and must not break successful retries.
  }
}

// Limit retry behavior to the one failure mode we know can be transient: the
// browser failed while reading an otherwise received JSON response body.
function isResponseReadFailure(error) {
  return error instanceof AuthedFetchError && error.responseFailureKind === "response-read-failed";
}

// Extract HTTP status from both AuthedFetchError instances and plain-ish error
// objects used in tests/callers.
export function getApiResponseStatus(error) {
  if (!error) {
    return null;
  }

  const status = error.response?.status ?? error.status;
  return typeof status === "number" ? status : null;
}

// Extract the endpoint URL from our enriched error first, then fall back to the
// Response URL when the error was created earlier in the fetch pipeline.
export function getApiResponseEndpoint(error) {
  if (!error) {
    return null;
  }

  return error.endpointUrl || error.response?.url || null;
}

// Decide which API errors should stay out of frontend Sentry. These are
// responses where the frontend is only observing a backend/API outcome. Keep
// unexpected 4xx responses reportable because they can indicate a broken
// client/server contract, but suppress 429 and 5xx duplication here.
export function isExpectedApiResponseError(error) {
  if (error instanceof RateLimitedError || error?.name === "RateLimtedError") {
    return true;
  }

  if (!(error instanceof AuthedFetchError)) {
    return false;
  }

  const status = getApiResponseStatus(error);
  return typeof status === "number" && status >= 500 && status < 600;
}

var Auth = {
  markLogoutInProgress,
  // Main JSON API wrapper. It injects the Firebase ID token and project context,
  // keeps client-only options out of the request body, classifies response-body
  // failures, and optionally retries transient body-read failures for critical
  // startup/bootstrap endpoints.
  fetchWithIdToken: async (url, params = {}, signal = null, options = {}) => {
    url = bypassProxyInDev(url);
    if (networkPolicyBlockedUrls.has(url)) {
      throw makeNetworkPolicyBlockedError("Network policy is blocking access to Sudowrite. Please switch networks or contact your administrator.", {
        endpointUrl: url,
        fromCooldown: true,
      });
    }

    const user = params.user;
    delete params.user;
    const {
      requestSource,
      suppressAbortSentry,
      responseReadFailureRetries,
      responseReadFailureRetryDelayMs,
    } = getAuthedFetchOptions(options);
    let idToken = null;
    let requestUserId = user?.uid;

    params.context_project_id = getCurrentProjectId(params);

    try {
      if (user) {
        // if user object is provided, get id token from that
        idToken = await user.getIdToken(false);
      } else if (firebase.auth().currentUser) {
        const user = firebase.auth().currentUser;
        requestUserId = user.uid;
        window.sudowriteUserId = getAuthUser()?.uid;
        window.sudowriteEmail = getAuthUser()?.email;
        // else we use currentUser (warning: this could result in race condition!)
        idToken = await user.getIdToken(false);
      } // else we send null for idtoken
    } catch (e) {
      console.log("ID TOKEN ERROR:");
      console.log(e);
      throw e;
    }
    window.sudowriteToken = idToken;

    params.idToken = idToken;
    const method = params.method || "POST";
    delete params.method;
    const requestBody = JSON.stringify(params);
    let lastResponseReadFailure = null;

    for (let attemptIndex = 0; attemptIndex <= responseReadFailureRetries; attemptIndex++) {
      try {
        const response = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            ...getVersionHeader(),
          },
          method: method,
          body: requestBody,
          signal: signal,
          credentials: "include",
        });

        if (!response.ok) {
          const readResult = await readResponseTextForDiagnostics(response);
          const responseText = readResult.responseText || "";
          const contentType = safeHeader(response, "content-type");
          if (!readResult.responseReadError && responseLooksLikeHtml(responseText, contentType) && isNetworkPolicyBlockedResponse(responseText, contentType)) {
            networkPolicyBlockedUrls.add(url);
            throw makeNetworkPolicyBlockedError("Network policy blocked this request. Please switch networks or contact your administrator.", {
              response,
              endpointUrl: url,
              responseText,
            });
          }

          if (response.status === 429) {
            throw new RateLimitedError();
          }
          throw await createServerProblemError(response, url, readResult);
        }

        const returnData = await parseJsonResponse(response, url);
        if (firebase.auth().currentUser?.uid === requestUserId &&
            isGenerationRequest({ method }, new URL(url, window.location.origin).pathname)) {
          clearEmailReviewRestriction(requestUserId);
        }
        if (lastResponseReadFailure) {
          await captureResponseReadRetryRecoverySafely(lastResponseReadFailure, {
            endpointUrl: lastResponseReadFailure.endpointUrl || lastResponseReadFailure.response?.url || url,
            requestSource,
            ...getResponseReadRetryContext(attemptIndex, responseReadFailureRetries),
          });
        }
        return returnData;
      } catch (e) {
        if (firebase.auth().currentUser?.uid === requestUserId) {
          recordEmailReviewRestriction(requestUserId, e.status, e.code);
        }
        const endpointUrl = e.endpointUrl || e.response?.url || url;
        const shouldRetryResponseReadFailure =
          isResponseReadFailure(e) &&
          attemptIndex < responseReadFailureRetries &&
          !signal?.aborted;

        if (shouldRetryResponseReadFailure) {
          lastResponseReadFailure = e;
          await delay(responseReadFailureRetryDelayMs * Math.pow(2, attemptIndex));
          continue;
        }

        await captureAuthedFetchExceptionSafely(e, {
          endpointUrl,
          requestSource,
          suppressAbortSentry,
          ...getResponseReadRetryContext(attemptIndex, responseReadFailureRetries),
        });
        throw e;
      }
    }
  },
  // Fetch binary/file responses with the same auth and project-context
  // injection as `fetchWithIdToken`, but return the response blob instead of
  // running the JSON parser.
  fetchFileWithIdToken: async (url, params = {}, signal = null) => {
    url = bypassProxyInDev(url);
    const user = params.user;
    delete params.user;
    let idToken = null;

    params.context_project_id = getCurrentProjectId(params);

    try {
      if (user) {
        // if user object is provided, get id token from that
        idToken = await user.getIdToken(false);
      } else if (firebase.auth().currentUser) {
        // else we use currentUser (warning: this could result in race condition!)
        idToken = await firebase.auth().currentUser.getIdToken(false);
      } // else we send null for idtoken
    } catch (e) {
      console.log("ID TOKEN ERROR:");
      console.log(e);
      throw e;
    }

    params.idToken = idToken;
    const method = params.method || "POST";
    delete params.method;

    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        ...getVersionHeader(),
      },
      method: method,
      body: JSON.stringify(params),
      signal: signal,
      credentials: "include",
    });

    if (!response.ok) {
      console.log("fetchWithIdToken: Bad response from server.");
      throw await createServerProblemError(response, url);
    }

    const returnData = await response.blob();
    return returnData;
  },

  // Stream server-sent completion chunks from endpoints that return newline-
  // delimited `data:` frames. This path keeps existing callback semantics while
  // routing JSON/error parsing through the shared diagnostic helpers where
  // possible.
  streamWithIdToken: (url, params, signal = null, onChunk, onDone, onError, nonce, onStart, options = null) => {
    url = bypassProxyInDev(url);
    let decoder = new TextDecoder("utf-8");
    let streamId = uniqueId();

    params.stream = true;
    if (nonce) {
      if (url.indexOf("?") === -1) {
        url += "?nonce=" + nonce;
      } else {
        url += "&nonce=" + nonce;
      }
    }

    params.context_project_id = getCurrentProjectId(params);

    return new Promise((resolve, reject) => {
      const requestUser = firebase.auth().currentUser;
      requestUser.getIdToken(false)
        .then((idToken) => {
          params.idToken = idToken;

          let buffer = null;

          fetch(url, {
            headers: {
              "Content-Type": "application/json",
              ...getVersionHeader(),
            },
            method: "POST",
            body: JSON.stringify(params),
            signal: signal,
            credentials: "include",
          })
            .then(async (response) => {
              // Not using status.ok because it could break 3xx responses

              if (response.status === CONTENT_POLICY_ERROR_CODE) {
                throw new Error(CONTENT_POLICY_ERROR, { cause: new ContentViolationError() });
              } else if (response.status >= 400) {
                const json = await parseJsonResponse(response, url);
                if (firebase.auth().currentUser?.uid === requestUser.uid) {
                  recordEmailReviewRestriction(requestUser.uid, response.status, json.code);
                }

                if (json.message) {
                  throw Object.assign(new Error(json.message), {
                    status: response.status,
                    code: typeof json.code === "string" ? json.code : undefined,
                  });
                } else {
                  throw new Error("An error has occurred. Please try again later.");
                }
              } else {
                if (response.status >= 200 && response.status < 300 &&
                    firebase.auth().currentUser?.uid === requestUser.uid &&
                    isGenerationRequest({ method: "POST" }, new URL(url, window.location.origin).pathname)) {
                  clearEmailReviewRestriction(requestUser.uid);
                }
                return response.body;
              }
            })
            .then((body) => {
              const reader = body.getReader();
              let result = "";
              let charsReceived = 0;
              let buffer = null;

              // Example:
              // data: {"id": "cmpl-O8AZS2LzSMhohadlkwSOL5Uo", "object": "text_completion", "created": 1598190220, "choices": [{"text": " it", "index": 4, "logprobs": null, "finish_reason": null}], "model": "davinci:2020-05-03"}
              onStart?.call(null);
              reader
                .read()
                .then(function processText({ done, value }) {
                  if (done) {
                    if (onDone) {
                      onDone();
                    }
                    return;
                  }

                  let val = decoder.decode(value);
                  // TODO: try using readline

                  if (val[val.length - 1] != "\n") {
                    console.log("Incomplete chunk!");

                    // Build up buffer until we get a complete data line
                    if (buffer) {
                      buffer += val;
                    } else {
                      buffer = val;
                    }
                  } else {
                    let seenDone = false;

                    // if there is a buffer from incomplete chunks, then we append them
                    if (buffer) {
                      console.log("Completing chunk: ");
                      val = buffer + val;
                      buffer = null;
                      console.log(val);
                    }

                    val.split("\n").forEach((line) => {
                      // TODO: push this logic into the server side

                      if (line.indexOf("[DONE]") !== -1) {
                        seenDone = true;
                      }

                      if (!seenDone) {
                        let json;
                        if (line.startsWith("data: ")) {
                          json = line.slice(6, line.length);
                        }

                        if (json) {
                          let returnData = JSON.parse(json);
                          //let choice = returnData.choices[0];
                          if (returnData.error) {
                            if (returnData.error === CONTENT_GENERATION_FAILED) {
                              throw new Error(returnData.error, { cause: new ContentViolationError() });
                            }
                            const msg =
                              streamPayloadErrorMessage(returnData.error)
                              ?? (typeof returnData.message === "string" && returnData.message.trim()
                                ? returnData.message.trim()
                                : null)
                              ?? "An error has occurred. Please try again later.";
                            throw new Error(msg);
                          }
                          //onText({ text: choice.text, index: choice.index, completionId: returnData.completionId });
                          onChunk(returnData, streamId);
                          // onText(returnData.choices);
                        }
                      }
                    });
                  }

                  return reader.read().then(processText);
                })
                .catch((e) => {
                  if (onError) {
                    onError({ error: true, message: e.message, cause: e.cause });
                  }
                });
            })
            .catch(async (e) => {
              if (e instanceof AuthedFetchError || e.isNetworkPolicyBlocked) {
                await captureAuthedFetchExceptionSafely(e, { endpointUrl: e.endpointUrl || e.response?.url || url });
              }
              if (onError) {
                onError({ error: true, message: e.message, cause: e.cause, status: e.status, code: e.code, responseFailureKind: e.responseFailureKind });
              }
            });
        });
    });
  },
  // Upload multipart form data with an ID token appended to the form body. The
  // fourth options argument mirrors `fetchWithIdToken` for telemetry controls,
  // while endpoint params remain separate from upload fields.
  postFilesWithIdToken: async (url, params = {}, formData, options = {}) => {
    url = bypassProxyInDev(url);
    const user = params.user;
    delete params.user;
    const { requestSource, suppressAbortSentry } = getAuthedFetchOptions(options);
    let idToken = null;

    params.context_project_id = getCurrentProjectId(params);

    try {
      if (user) {
        // if user object is provided, get id token from that
        idToken = await user.getIdToken(false);
      } else if (firebase.auth().currentUser) {
        // else we use currentUser (warning: this could result in race condition!)
        idToken = await firebase.auth().currentUser.getIdToken(false);
      } // else we send null for idtoken
    } catch (e) {
      console.log("ID TOKEN ERROR:");
      console.log(e);
      throw e;
    }

    params.idToken = idToken;
    const method = params.method || "POST";
    delete params.method;

    formData.append("idToken", params.idToken);

    try {
      const response = await fetch(url, {
        method: method,
        headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        throw await createServerProblemError(response, url);
      }

      const returnData = await parseJsonResponse(response, url);
      return returnData;
    } catch (e) {
      const endpointUrl = e.endpointUrl || e.response?.url || url;
      await captureAuthedFetchExceptionSafely(e, { endpointUrl, requestSource, suppressAbortSentry });
      throw e;
    }
  },
};

// Prefer an explicit projectId from endpoint params, then fall back to the
// current editor route. This keeps backend context populated for calls made from
// project pages without forcing every caller to thread projectId manually.
const getCurrentProjectId = (params) => {
  if (params?.projectId) {
    return params.projectId;
  }

  const location = window.location.pathname;
  const match = matchPath(location, {
    path: "/app/project/:projectId",
    exact: false,
    strict: false,
  });

  return match?.params?.projectId || null;
};

export default Auth;
